import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaPlus, FaList, FaImage,
  FaHome, FaBox,
  FaPenFancy,FaCalendarAlt,FaChartBar,FaRupeeSign, FaVideo , FaQuoteLeft, FaLeaf,FaHandHoldingHeart,} from 'react-icons/fa'; 
import { LayoutDashboard, LogOut, ChevronLeft, Menu, Shield, X } from 'lucide-react';
import { actionType } from '../context/Reducer';
import { toast } from 'react-toastify';
import { useStateValue } from '../context/StateProvider';
import { signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, firestore } from '../firebase.config';
import { getDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { IoCashOutline } from 'react-icons/io5';
import { BiCreditCard } from 'react-icons/bi';
import { GiRoyalLove } from 'react-icons/gi';

const Sidebar = ({ isOpen, toggleSidebar, userType }) => {
  const location = useLocation();
  const { usertype } = location.state || {};

  const navigate = useNavigate();
  const [{ user }, dispatch] = useStateValue();

  const logout = async () => {
    const confirmLogout = window.confirm("Are you sure you want to logout?");
    if (!confirmLogout) return;

    try {
      const user = auth.currentUser;
      if (!user || !user.email) return;

      const updateLogoutTimestamp = async (collectionName) => {
        const userRef = collection(firestore, collectionName);
        const userQuery = query(userRef, where('email', '==', user.email));
        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          const username = userData.username;

          if (username) {
            const userDocRef = doc(firestore, collectionName, username);
            const currentDateTime = new Date();
            const formattedDate = currentDateTime.toISOString().split('T')[0];
            const formattedTime = currentDateTime.toLocaleTimeString('en-US', { hour12: true });

            await updateDoc(userDocRef, {
              [`loginHistory.${formattedDate}.LogoutTime`]: formattedTime,
            });
          }
        }
      };

      await Promise.all([
        updateLogoutTimestamp('Staff'),
        updateLogoutTimestamp('users')
      ]);

      localStorage.clear();
      dispatch({ type: actionType.SET_USER, user: null });
      toast.success("Successfully logged out!");
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  };

  const allMenuItems = [
    { icon: FaHome, label: 'Add Home Slider', path: '/homeslider' },
    { icon: FaLeaf, label: 'Add Category', path: '/addcategory' },
    { icon: FaHandHoldingHeart, label: 'Donation In Kind', path: '/donationinkind' },
     { icon:  FaBox,label: 'Add Product', path: '/addproduct' },
    { icon: FaImage, label: 'Add Gallery', path: '/gallery' },
    { icon: FaVideo, label: 'Video Gallery', path: '/videogallery' },
    { icon: FaCalendarAlt, label: 'Add Events', path: '/event' },
    { icon: GiRoyalLove, label: 'Lord Krishna', path: '/lordkrishna' },
    { icon: FaQuoteLeft, label: 'Testimonials', path: '/testimonials' },
    { icon: FaChartBar, label: 'Add Metrics', path: '/metrics' },
    { icon: IoCashOutline, label: 'Add Donation Amounts', path: '/DonationAmounts' },
    { icon: BiCreditCard, label: 'Add Expense', path: '/addexpense' },
    { icon: FaRupeeSign, label: 'Donate Now', path: '/donatenow' },
  ];

  const menuItems = userType === 'Accountant'
    ? allMenuItems.filter(item => item.label === 'View Orders')
    : allMenuItems;

  return (
    <>
      {/* Mobile top menu button */}
      {!isOpen && (
        <button
          className="fixed top-0 left-0 z-50 w-full max-w-full lg:hidden bg-white rounded-lg shadow-lg border border-gray-200 py-3 pl-4 pr-3 flex items-center transition duration-300 hover:shadow-xl hover:bg-[#e0f2e9] focus:ring-2 focus:ring-offset-2 focus:ring-[#15803d]"
          onClick={toggleSidebar}
        >
          <Menu className="w-6 h-6 text-blue-700" />
          <span className="ml-2 text-sm font-medium text-[#ea764d]">Menu</span>
        </button>
      )}

      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={() => toggleSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-white text-[#fcaf17] shadow-xl z-40 flex flex-col
          transition-all duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${isOpen ? 'w-56' : 'lg:w-20 w-56'}`}
      >
        {/* Header — fixed height */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-[#fcaf17]">
          <div className={`flex items-center gap-3 ${!isOpen && 'lg:justify-center'}`}>
            <div className="lg:w-8 lg:h-8 w-6 h-6 bg-white hover:bg-[#fcaf17] rounded-lg flex items-center justify-center">
              <Shield className="lg:h-5 lg:w-5 w-4 h-4 text-[#fcaf17] hover:text-white" />
            </div>
            <span className={`font-bold lg:text-lg text-sm ${!isOpen && 'lg:hidden'}`}>Admin Panel</span>
          </div>
          <button
            onClick={() => toggleSidebar(!isOpen)}
            aria-label="Toggle Sidebar"
            className="p-1 hover:bg-[#fcaf17] hover:text-white rounded-lg transition-colors"
          >
            {isOpen ? <ChevronLeft className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Scrollable Navigation — takes remaining space */}
        <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin scrollbar-thumb-[#fcaf17]/40 scrollbar-track-transparent">
          <ul className="space-y-1">
            {menuItems.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.path || '#'}
                  onClick={() => {
                    // Close sidebar on mobile after clicking a link
                    if (window.innerWidth < 1024) toggleSidebar(false);
                  }}
                  className={`flex items-center gap-3 p-3 lg:text-[13px] text-[12px] rounded-lg transition-colors
                    ${location.pathname === item.path
                      ? 'bg-[#fcaf17] text-white'
                      : 'hover:bg-[#fcaf17] hover:text-white text-[#fcaf17]'}
                    ${!isOpen && 'lg:justify-center'}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className={`whitespace-nowrap ${!isOpen && 'lg:hidden'}`}>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout — fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-[#fcaf17]">
          <button
            onClick={logout}
            className={`flex items-center gap-3 p-3 lg:text-md text-[15px] rounded-lg text-[#fcaf17] hover:bg-[#fcaf17] hover:text-white w-full transition-colors
              ${!isOpen && 'lg:justify-center'}`}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span className={`${!isOpen && 'lg:hidden'}`}>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;